import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Badge, Row, Col, Form, Nav } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../services/api';
import ApprovalChip from './ApprovalChip';
import { useAuth } from '../context/AuthContext';
import Pager from './Pager';
import OrderRequestsPanel from './OrderRequestsPanel';
import UnbilledConducesPanel from './UnbilledConducesPanel';
import ClientDealsPanel from './ClientDealsPanel';
import { formatDay } from '../utils/dates';

const PAGE_SIZE = 10;
const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);
const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const STATUS = { pending: { label: 'Por entregar', bg: 'warning' }, delivered: { label: 'Entregado', bg: 'success' } };

const ConducesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  const [conduces, setConduces] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ['pedidos', 'sin_facturar', ...(isAdmin ? ['acuerdos'] : [])].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'conduces';
  const [openOrders, setOpenOrders] = useState(null);

  const loadOrderCount = useCallback(() => API.get('/order-requests/count').then(r => setOpenOrders(r.data)).catch(() => {}), []);
  useEffect(() => { loadOrderCount(); }, [loadOrderCount]);
  const chooseTab = (key) => setSearchParams(key === 'conduces' ? {} : { tab: key }, { replace: true });

  // "Sin facturar": delivered conduces whose pedido has no factura (loaded here so the tab shows how many).
  const [unbilled, setUnbilled] = useState(null);
  const [unbilledError, setUnbilledError] = useState('');
  const loadUnbilled = useCallback(() => API.get('/conduces/unbilled').then(r => { setUnbilled(r.data); setUnbilledError(''); })
    .catch(() => setUnbilledError('No se pudieron cargar los conduces sin facturar.')), []);
  useEffect(() => { loadUnbilled(); }, [loadUnbilled]);

  const load = useCallback(() => API.get('/conduces').then(r => setConduces(r.data)).catch(() => setError('No se pudieron cargar los conduces.')), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const term = norm(search.trim());
  const filtered = useMemo(() => (conduces || []).filter(c => !term
    || [c.number, c.client?.name, c.client?.rnc, c.reference].some(v => norm(v).includes(term))), [conduces, term]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const pendingCount = (conduces || []).filter(c => c.status === 'pending').length;
  const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const deliveredThisMonth = (conduces || []).filter(c => c.status === 'delivered' && c.deliveredAt && new Date(c.deliveredAt) >= monthStart).length;

  const run = async (id, fn, okMessage) => {
    setBusyId(id); setError(''); setNotice('');
    // A 202 means it was asked of an admin instead of done (the toast says so): no "done" message.
    try { const res = await fn(); if (okMessage && res?.status !== 202) setNotice(okMessage); await load(); }
    catch (err) { setError(err.response?.data?.message || 'La acción falló.'); }
    finally { setBusyId(''); }
  };
  const markDelivered = (c) => run(c._id, () => API.patch(`/conduces/${c._id}/status`, { status: 'delivered' }), `Conduce ${c.number} marcado como entregado.`);
  const markPending = (c) => run(c._id, () => API.patch(`/conduces/${c._id}/status`, { status: 'pending' }), `Conduce ${c.number} vuelve a estar por entregar.`);
  const remove = (c) => {
    if (!window.confirm(`¿Eliminar el conduce ${c.number}?`)) return;
    run(c._id, () => API.delete(`/conduces/${c._id}`), `Conduce ${c.number} eliminado.`);
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="app-title" style={{ textAlign: 'left', margin: 0 }}>Conduces</h2>
        <Button onClick={() => navigate('/conduces/new')}>+ Nuevo Conduce</Button>
      </div>

      <Nav variant="tabs" activeKey={tab} onSelect={chooseTab} className="mb-3">
        <Nav.Item><Nav.Link eventKey="conduces">Conduces</Nav.Link></Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="pedidos">
            Pedidos
            {openOrders?.open > 0 && <Badge bg={openOrders.pending > 0 ? 'warning' : 'info'} pill className="ms-2" title="Por atender">{openOrders.open}</Badge>}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="sin_facturar">
            Sin facturar
            {unbilled?.length > 0 && <Badge bg="secondary" pill className="ms-2" title="Conduces entregados sin factura">{unbilled.length}</Badge>}
          </Nav.Link>
        </Nav.Item>
        {isAdmin && <Nav.Item><Nav.Link eventKey="acuerdos">Acuerdos</Nav.Link></Nav.Item>}
      </Nav>

      {tab === 'sin_facturar' && <UnbilledConducesPanel rows={unbilled} error={unbilledError} onChanged={loadUnbilled} />}
      {tab === 'acuerdos' && <ClientDealsPanel />}
      {tab === 'pedidos' ? <OrderRequestsPanel onCountChange={loadOrderCount} counts={openOrders} /> : tab === 'conduces' && (<>

      <Row className="mb-3">
        <Col md={6} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Por entregar</div>
            <h2 className="mt-2 mb-0" style={{ color: pendingCount ? 'var(--mm-yellow)' : undefined }}>{conduces ? pendingCount : '…'}</h2>
            {openOrders?.open > 0 && (
              <button type="button" className="btn btn-link p-0 small mt-1" style={{ color: 'var(--mm-cyan)' }} onClick={() => chooseTab('pedidos')}>
                {openOrders.open} {openOrders.open === 1 ? 'pedido nuevo' : 'pedidos nuevos'} de CG Connect →
              </button>
            )}
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Entregados este mes</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-green)' }}>{conduces ? deliveredThisMonth : '…'}</h2>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm p-3">
        {error && <div className="alert alert-danger">{error}</div>}
        {notice && <div className="alert alert-success">{notice}</div>}
        <Form.Control className="mb-3" style={{ maxWidth: 360 }} placeholder="Buscar por número, cliente o referencia..." value={search} onChange={e => setSearch(e.target.value)} />
        {conduces === null ? <div className="text-muted py-3">Cargando...</div> : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr><th>No.</th><th>Cliente</th><th>Fecha</th><th>Referencia</th><th>Precios</th><th>Estado</th><th /></tr>
              </thead>
              <tbody>
                {slice.map(c => (
                  <tr key={c._id}>
                    <td><strong>{c.number}</strong> <ApprovalChip entityId={c._id} className="ms-1" /></td>
                    <td>{c.client?.name || '—'}</td>
                    <td className="small">{dateStr(c.createdAt)}</td>
                    <td className="small">{c.reference || '—'}</td>
                    <td className="small">{c.showPrices ? money(c.total) : <span className="text-muted">Sin precios</span>}</td>
                    <td>
                      <Badge bg={STATUS[c.status]?.bg || 'secondary'}>{STATUS[c.status]?.label || c.status}</Badge>
                      {c.status === 'delivered' && c.deliveredAt && <div className="small text-muted">{dateStr(c.deliveredAt)}</div>}
                    </td>
                    <td className="text-end">
                      <div className="d-flex gap-2 justify-content-end flex-wrap">
                        {c.status === 'pending' && (
                          <Button size="sm" variant="outline-success" disabled={busyId === c._id} onClick={() => markDelivered(c)}><i className="bi bi-check2-circle" /> Entregado</Button>
                        )}
                        {c.status === 'delivered' && isAdmin && (
                          <Button size="sm" variant="outline-secondary" disabled={busyId === c._id} title="Volver a poner como por entregar" onClick={() => markPending(c)}><i className="bi bi-arrow-counterclockwise" /></Button>
                        )}
                        <Button size="sm" variant="outline-secondary" onClick={() => window.open(`/conduces/${c._id}/print`, '_blank')}><i className="bi bi-printer" /> Ver / Imprimir</Button>
                        {c.status === 'pending' && (
                          <Button size="sm" variant="outline-secondary" title="Editar" onClick={() => navigate(`/conduces/${c._id}/edit`)}><i className="bi bi-pencil" /></Button>
                        )}
                        <Button size="sm" variant="outline-danger" title="Eliminar" disabled={busyId === c._id} onClick={() => remove(c)}><i className="bi bi-trash" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {slice.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted py-4">{term ? `Ningún conduce coincide con "${search.trim()}".` : 'Sin conduces todavía.'}</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
        {pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">Página {current} de {pages} — {filtered.length} conduce{filtered.length === 1 ? '' : 's'}</span>
            <Pager page={current} pages={pages} onChange={setPage} />
          </div>
        )}
      </Card>
      </>)}
    </div>
  );
};

export default ConducesPage;
